import loadable from '@loadable/component';
import $ from 'jquery';
import { useEffect, useState } from 'react';
import { useNavigate, useRoutes, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useTypedSelector } from './hooks/store';
import useDomain from './hooks/useDomain';
import {
  changeIsSkytrakApp,
  changeIsSkytrakIOS,
  changeIsTabletApp,
  changeUrlProShop,
  selectIsSkytrakApp,
  selectIsSkytrakIOS,
  selectIsTabletApp,
  selectUrlProShop,
} from './slices/config';
import { setBaseLocale, setLocale } from './slices/language';
import { selectTokenHubSpot } from './slices/loadConfiguration';
import {
  fetchUserData,
  selectAuthenticationStatus,
  selectUserData,
} from './slices/user';

const PageWrapper = loadable(() => import('./components/PageWrapper'));
const PrivateRoute = loadable(() => import('./components/PrivateRoute'));
const PublicRoute = loadable(() => import('./components/PublicRoute'));
const PublicPage = loadable(() => import('./components/PublicPage'));
const Home = loadable(() => import('./pages/Home'));
const NotFoundPage = loadable(() => import('./pages/NotFound'));
const SignInPage = loadable(() => import('./pages/SignIn'));
const ChildrenPrivacy = loadable(() => import('./pages/ChildrenPrivacy'));
const ForgotPassword = loadable(() => import('./pages/ForgotPassword'));
const ResetPassword = loadable(() => import('./pages/ResetPassword'));
const VerifyEmail = loadable(() => import('./pages/VerifyChangeEmail'));
const AgeAffirmation = loadable(() => import('./pages/AgeAffirmation'));
const MyAccount = loadable(() => import('./pages/MyAccount'));
const Referral = loadable(() => import('./pages/Referral'));
const SharedVideoLesson = loadable(() => import('./pages/SharedVideoLesson'));
const ErrorBoundaryWrapper = loadable(
  () => import('./components/ErrorBoundary')
);
const MyGameV2 = loadable(() => import('./pages/MyGameV2'));
const Profile = loadable(() => import('./pages/MyAccount/Profile'));
const Account = loadable(() => import('./pages/MyAccount/Account'));
const PurchaseHistory = loadable(
  () => import('./pages/MyAccount/Account/PurchaseHistory')
);
const UserAgreement = loadable(
  () => import('./pages/MyAccount/Account/UserAgreement')
);
const Help = loadable(() => import('./pages/MyAccount/Help'));
const ChangePassword = loadable(
  () => import('./pages/MyAccount/Profile/ChangePassword')
);
const FamilyAccount = loadable(
  () => import('./pages/MyAccount/Account/FamilyAccount')
);

const About = loadable(() => import('./pages/MyAccount/Help/About'));
const FAQ = loadable(() => import('./pages/MyAccount/Help/FAQ'));
const Care = loadable(() => import('./pages/MyAccount/Help/Care'));
const SatisfactionSurvey = loadable(
  () => import('./pages/MyAccount/Account/SatisfactionSurvey')
);
const SkyTrakMyDevice = loadable(() => import('./pages/SkyTrak/MyDevice'));

const MyBagsV2 = loadable(() => import('./pages/MyGameV2/MyBagsV2'));
const FetchDataCH = loadable(() => import('./components/FetchDataCH'));
const Activity = loadable(() => import('./pages/MyGameV2/Activity'));
const UniversalSignUp = loadable(() => import('./pages/Universal/SignUp'));
const SkyTrakSignUp = loadable(() => import('./pages/SkyTrak/Registration'));
const TermsConditions = loadable(() => import('./pages/TermsConditions'));
const GTShop = loadable(() => import('./pages/GTShop'));
const ProfileInformation = loadable(
  () => import('./pages/MyAccount/Profile/ProfileInformation')
);

const MembershipsSkyTrak = loadable(
  () => import('./pages/SkyTrak/MembershipsSkyTrak')
);
const ExploreSkyTrak = loadable(() => import('./pages/SkyTrak/ExploreSkyTrak'));
const PurchaseMembershipsSkyTrak = loadable(
  () => import('./pages/SkyTrak/PurchaseMembershipsSkyTrak')
);

function App() {
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const skytrakTicket = searchParams.get('skytrakTicket');
  const ticket = searchParams.get('ticket');
  const redirectLink = searchParams.get('redirect');
  const isAuthenticated = useTypedSelector(selectAuthenticationStatus);
  const langFromUrl = searchParams.get('lang');
  const location = window.location.hostname;
  const isTablet = searchParams.get('isTablet');
  const isSkytrakapp = searchParams.get('skytrakapp');
  const isSkyTrakIos = searchParams.get('isSkyTrak');
  const isSkytrakIos = searchParams.get('isSkytrak');
  const urlProShop = searchParams.get('urlProShop');
  const isTabletApp = useTypedSelector(selectIsTabletApp);
  const isSkytrakApp = useTypedSelector(selectIsSkytrakApp);
  const isSkytrakIOS = useTypedSelector(selectIsSkytrakIOS);
  const urlProShopApp = useTypedSelector(selectUrlProShop);
  const domain = useDomain();
  const [loadLang, setLoadLang] = useState(false);
  const userData = useTypedSelector(selectUserData);
  const tokenHubSpot = useTypedSelector(selectTokenHubSpot);

  useEffect(() => {
    if (isTablet === 'true' && !isTabletApp) {
      dispatch(changeIsTabletApp(true));
    }
  }, [dispatch, isTablet, isTabletApp]);

  useEffect(() => {
    if (isSkytrakapp === 'true' && !isSkytrakApp) {
      dispatch(changeIsSkytrakApp(true));
    }
  }, [dispatch, isSkytrakApp, isSkytrakapp]);

  useEffect(() => {
    if ((isSkytrakIos === 'true' || isSkyTrakIos === 'true') && !isSkytrakIOS) {
      dispatch(changeIsSkytrakIOS(true));
    }
  }, [dispatch, isSkyTrakIos, isSkytrakIOS, isSkytrakIos]);

  useEffect(() => {
    if (!urlProShopApp && urlProShop && isTabletApp) {
      dispatch(changeUrlProShop(urlProShop));
      navigate('/ecommerce');
    }
  }, [dispatch, navigate, urlProShop, urlProShopApp, isTabletApp]);

  useEffect(() => {
    if (!loadLang) {
      if (!langFromUrl) {
        if (location.match(/localhost|.com/)) {
          if (location.match(/gpro/)) {
            dispatch(setLocale('ja'));
            dispatch(setBaseLocale('ja-JP'));
          } else {
            dispatch(setLocale('en'));
            dispatch(setBaseLocale('en-US'));
          }
        } else if (location.match(/.hk/)) {
          dispatch(setLocale('en'));
          dispatch(setBaseLocale('en-HK'));
        } else if (location.match(/.sg/)) {
          dispatch(setLocale('en'));
          dispatch(setBaseLocale('en-SG'));
        } else if (location.match(/golftec.ca/)) {
          dispatch(setLocale('fr'));
          dispatch(setBaseLocale('fr-CA'));
        } else if (location.match(/.jp/)) {
          dispatch(setLocale('ja'));
          dispatch(setBaseLocale('ja-JP'));
        } else if (location.match(/.cn/)) {
          dispatch(setLocale('zh'));
          dispatch(setBaseLocale('zh-CN'));
        } else if (location.match(/.ae/)) {
          dispatch(setLocale('en'));
          dispatch(setBaseLocale('en-AE'));
        } else if (location.match(/.za/)) {
          dispatch(setLocale('en'));
          dispatch(setBaseLocale('en-ZA'));
        }
      } else {
        if (location.match(/localhost|.com/)) {
          if (location.match(/gpro/)) {
            dispatch(setLocale(langFromUrl === 'ja' ? 'ja' : 'en'));
            dispatch(setBaseLocale(langFromUrl === 'ja' ? 'ja-JP' : 'en-JP'));
          } else {
            dispatch(setLocale('en'));
            dispatch(setBaseLocale('en-US'));
          }
        } else if (location.match(/.hk/)) {
          dispatch(setLocale('en'));
          dispatch(setBaseLocale('en-HK'));
        } else if (location.match(/.sg/)) {
          dispatch(setLocale('en'));
          dispatch(setBaseLocale('en-SG'));
        } else if (location.match(/golftec.ca/)) {
          dispatch(setLocale(langFromUrl === 'fr' ? 'fr' : 'en'));
          dispatch(setBaseLocale(langFromUrl === 'fr' ? 'fr-CA' : 'en-CA'));
        } else if (location.match(/.jp/)) {
          dispatch(setLocale(langFromUrl === 'ja' ? 'ja' : 'en'));
          dispatch(setBaseLocale(langFromUrl === 'ja' ? 'ja-JP' : 'en-JP'));
        } else if (location.match(/.cn/)) {
          dispatch(setLocale(langFromUrl === 'zh' ? 'zh' : 'en'));
          dispatch(setBaseLocale(langFromUrl === 'zh' ? 'zh-CN' : 'en-CN'));
        } else if (location.match(/.ae/)) {
          dispatch(setLocale('en'));
          dispatch(setBaseLocale('en-AE'));
        } else if (location.match(/.za/)) {
          dispatch(setLocale('en'));
          dispatch(setBaseLocale('en-ZA'));
        }
      }
      setLoadLang(true);
    }
  }, [dispatch, langFromUrl, loadLang, location]);

  useEffect(() => {
    if (redirectLink && !isTablet) {
      window.location.href = redirectLink;
    }
  }, [redirectLink, isTablet]);

  useEffect(() => {
    if (skytrakTicket) {
      sessionStorage.setItem('skytrakTicket', skytrakTicket);
    }
  }, [skytrakTicket]);

  useEffect(() => {
    if (isAuthenticated && !ticket && !skytrakTicket) {
      dispatch(fetchUserData());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      $('html').addClass('login');
    } else {
      $('html').removeClass('login');
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const script = document.createElement('script');
    const existingScript = document.getElementById('hs-script-loader');

    if (isAuthenticated) {
      if (userData && userData?.email && tokenHubSpot) {
        window.hsConversationsSettings = {
          loadImmediately: false,
          enableWidgetCookieBanner: false,
          disableAttachment: false,
          identificationEmail: userData?.email,
          identificationToken: tokenHubSpot,
        };
        window.hsConversationsOnReady = [
          () => {
            if (
              window.HubSpotConversations &&
              window.HubSpotConversations.widget
            ) {
              window.HubSpotConversations.widget.load({ widgetOpen: false });
              window.HubSpotConversations.widget.close();
            }
          },
        ];

        if (!existingScript) {
          script.type = 'text/javascript';
          script.id = 'hs-script-loader';
          script.src = 'https://js-na1.hs-scripts.com/41352146.js';
          script.async = true;
          document.head.appendChild(script);
        } else {
          window.HubSpotConversations &&
            window.HubSpotConversations.resetAndReloadWidget();
        }
      }
    } else {
      window.hsConversationsSettings = {
        loadImmediately: false,
        enableWidgetCookieBanner: false,
        disableAttachment: false,
      };
      window.hsConversationsOnReady = [
        () => {
          if (
            window.HubSpotConversations &&
            window.HubSpotConversations.widget
          ) {
            window.HubSpotConversations.widget.load({ widgetOpen: false });
            window.HubSpotConversations.widget.close();
          }
        },
      ];

      if (!existingScript) {
        script.type = 'text/javascript';
        script.id = 'hs-script-loader';
        script.src = 'https://js-na1.hs-scripts.com/41352146.js';
        script.async = true;
        document.head.appendChild(script);
      } else {
        // window.HubSpotConversations.clear({ resetWidget: true });
        window.HubSpotConversations &&
          window.HubSpotConversations.resetAndReloadWidget();
      }
    }
  }, [isAuthenticated, tokenHubSpot, userData]);
  const isSkytrak = domain === 'skytrak';

  const routes = [
    {
      element: <PublicRoute />,
      children: [
        { path: '/login', element: <SignInPage /> },
        { path: '/sign-up', element: <UniversalSignUp /> },
        { path: '/register', element: <SkyTrakSignUp /> },
        { path: '/age-affirmation', element: <AgeAffirmation /> },
        { path: '/verify-email', element: <VerifyEmail /> },
        { path: '/children-privacy', element: <ChildrenPrivacy /> },
        { path: '/forgot-password', element: <ForgotPassword /> },
        { path: '/reset-password', element: <ResetPassword /> },
        { path: '/terms-and-conditions', element: <TermsConditions /> },
      ],
    },
    {
      element: <PrivateRoute />,
      children: [
        {
          element: <FetchDataCH />,
          children: [
            {
              element: <PageWrapper />,
              children: [
                {
                  element: <ErrorBoundaryWrapper />,
                  children: [
                    {
                      path: '/',
                      children: [
                        { path: '/ecommerce', element: <GTShop /> },
                        { index: true, element: <Home /> },
                        {
                          element: <MyAccount />,
                          children: [
                            {
                              element: <Profile />,
                              children: [
                                {
                                  path: 'my-profile',
                                  element: <ProfileInformation />,
                                  children: [
                                    ...(isSkytrak ? [{ path: 'delete' }] : []),
                                  ],
                                },
                                {
                                  path: 'change-password',
                                  element: <ChangePassword />,
                                },
                                ...(isSkytrak
                                  ? [
                                      {
                                        path: 'purchased-history',
                                        element: <PurchaseHistory />,
                                        children: [{ path: ':id' }],
                                      },
                                    ]
                                  : []),
                              ],
                            },
                            {
                              element: <Account />,
                              children: [
                                {
                                  path: 'family-account',
                                  element: <FamilyAccount />,
                                },
                                {
                                  path: 'user-agreement',
                                  element: <UserAgreement />,
                                },
                                {
                                  path: 'satisfaction-survey',
                                  element: <SatisfactionSurvey />,
                                },
                              ],
                            },
                            {
                              element: <Help />,
                              children: [
                                { path: 'golftec-cares', element: <Care /> },
                                { path: 'about-golftec', element: <About /> },
                                { path: 'faq', element: <FAQ /> },
                              ],
                            },
                          ],
                        },

                        {
                          element: <MyGameV2 />,
                          children: [
                            {
                              path: 'activity',
                              element: <Activity />,
                              children: [
                                { path: ':id', element: <Activity /> },
                                {
                                  path: 'multiple-activities',
                                  element: <Activity />,
                                },
                              ],
                            },
                            { path: 'my-bag', element: <MyBagsV2 /> },
                          ],
                        },
                        {
                          path: 'my-device',
                          element: <SkyTrakMyDevice />,
                        },
                        ...(isSkytrak
                          ? [
                              {
                                path: 'memberships',
                                element: <MembershipsSkyTrak />,
                              },
                              {
                                path: 'purchase-memberships',
                                element: <PurchaseMembershipsSkyTrak />,
                              },
                              {
                                path: 'explore-skytrak',
                                element: <ExploreSkyTrak />,
                              },
                            ]
                          : []),
                        { path: 'help', element: <Home /> },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    { path: '*', element: <NotFoundPage /> },
    {
      element: <PublicPage />,
      children: [
        { path: 'referral', element: <Referral /> },
        { path: 'share/:language/:id', element: <SharedVideoLesson /> },
      ],
    },
  ];
  return useRoutes(routes);
}

export default App;
